import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRight, RefreshCw } from 'lucide-react';
import type { GithubIssueDto } from '@aiai/shared';
import { fetchGithubIssues, pullInGithubIssue } from '../api';
import { qk } from '../api/queryKeys';
import { errorMessage } from '../lib/api';
import { configError } from '../lib/configError';
import { snippet } from '../lib/format';
import { Modal } from './Modal';
import { Badge } from './Badge';
import { Callout } from './Callout';
import { ConfigErrorNotice } from './ConfigErrorNotice';
import { EmptyState, ErrorState, LoadingState } from './States';
import { Row, RowList } from './Row';
import { Spinner } from './Spinner';
import { useWorkspacePath } from '../hooks/useWorkspacePath';

function GithubIssueRow({
  issue,
  busy,
  onPullIn,
}: {
  issue: GithubIssueDto;
  busy: boolean;
  onPullIn: () => void;
}) {
  const description =
    snippet(issue.body) ||
    (issue.labels.length > 0 ? issue.labels.join(' · ') : `#${issue.number}`);

  return (
    <Row
      href={issue.htmlUrl}
      badge={
        <Badge tone={issue.state === 'closed' ? 'neutral' : 'info'} size="sm">
          {issue.state === 'closed' ? 'Closed' : 'Open'}
        </Badge>
      }
      title={
        <>
          <span className="text-ink-faint">#{issue.number}</span> {issue.title}
        </>
      }
      description={description}
      actions={
        issue.pulledIn ? (
          <Badge tone="success" size="sm">
            Pulled in
          </Badge>
        ) : (
          <button
            type="button"
            className="btn-secondary btn-sm"
            onClick={onPullIn}
            disabled={busy}
          >
            Pull in
            {busy ? (
              <Spinner className="h-3.5 w-3.5" />
            ) : (
              <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
            )}
          </button>
        )
      }
    />
  );
}

/**
 * One-time migration surface: browse the linked repo's GitHub issues and pull
 * one into the loop (which is how a loop issue gets created from an existing
 * repo issue). Lives behind the project's "⋯" menu rather than a persistent
 * tab — pulling issues in is a setup step, not day-to-day work.
 */
export function MigrateIssuesDialog({
  open,
  onClose,
  projectId,
  hasCredential,
}: {
  open: boolean;
  onClose: () => void;
  projectId: string;
  hasCredential: boolean;
}) {
  const navigate = useNavigate();
  const { wp } = useWorkspacePath();
  const queryClient = useQueryClient();
  const [githubState, setGithubState] = useState<'open' | 'closed' | 'all'>('open');

  const githubIssuesQuery = useQuery({
    queryKey: ['github-issues', projectId, githubState],
    queryFn: () => fetchGithubIssues(projectId, { state: githubState }),
    enabled: open && hasCredential && !!projectId,
  });

  const pullIn = useMutation({
    mutationFn: (number: number) => pullInGithubIssue(projectId, number),
    onSuccess: (issue) => {
      queryClient.invalidateQueries({ queryKey: qk.projectIssues(projectId) });
      queryClient.invalidateQueries({ queryKey: ['github-issues', projectId] });
      onClose();
      navigate(wp(`/issues/${issue.code ?? issue.id}`));
    },
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Migrate GitHub issues"
      widthClass="max-w-xl"
    >
      {!hasCredential ? (
        <Callout tone="warning">
          Add a GitHub token (with Issues read &amp; write) to this project or in
          Settings to browse and pull in repo issues.
        </Callout>
      ) : (
        <>
          <div className="mb-3 flex items-center justify-between gap-2">
            <p className="min-w-0 text-xs text-ink-faint">
              Browse the linked repo's GitHub issues and pull one in to run the
              pipeline against it.
            </p>
            <div className="flex flex-none items-center gap-2">
              <select
                className="input h-8 py-0 text-xs"
                value={githubState}
                onChange={(e) =>
                  setGithubState(e.target.value as 'open' | 'closed' | 'all')
                }
                aria-label="Filter GitHub issues by state"
              >
                <option value="open">Open</option>
                <option value="closed">Closed</option>
                <option value="all">All</option>
              </select>
              <button
                type="button"
                className="btn-ghost btn-sm"
                onClick={() => githubIssuesQuery.refetch()}
                disabled={githubIssuesQuery.isFetching}
              >
                {githubIssuesQuery.isFetching ? (
                  <Spinner className="h-3.5 w-3.5" />
                ) : (
                  <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
                )}{' '}
                Refresh
              </button>
            </div>
          </div>

          {pullIn.isError &&
            (configError(pullIn.error) ? (
              <div className="mb-2">
                <ConfigErrorNotice error={configError(pullIn.error)!} />
              </div>
            ) : (
              <Callout tone="danger" className="mb-2">
                {errorMessage(pullIn.error, 'Could not pull in issue')}
              </Callout>
            ))}

          {githubIssuesQuery.isLoading ? (
            <LoadingState label="Loading GitHub issues…" />
          ) : githubIssuesQuery.isError ? (
            configError(githubIssuesQuery.error) ? (
              <ConfigErrorNotice error={configError(githubIssuesQuery.error)!} />
            ) : (
              <ErrorState
                message={errorMessage(
                  githubIssuesQuery.error,
                  'Failed to load GitHub issues',
                )}
                onRetry={() => githubIssuesQuery.refetch()}
              />
            )
          ) : (githubIssuesQuery.data ?? []).length === 0 ? (
            <EmptyState
              compact
              title="No matching issues"
              description="Create issues in the repo (or change the filter) to pull them into the loop."
            />
          ) : (
            <RowList>
              {(githubIssuesQuery.data ?? []).map((issue) => (
                <GithubIssueRow
                  key={issue.number}
                  issue={issue}
                  busy={pullIn.isPending && pullIn.variables === issue.number}
                  onPullIn={() => pullIn.mutate(issue.number)}
                />
              ))}
            </RowList>
          )}
        </>
      )}
    </Modal>
  );
}
